import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { MapLibreMap, MapMarker } from '@molecules/index';
import { Map } from 'maplibre-gl';
import { MainLayout } from '@templates/index';
import { Card, Badge, Button } from '@atoms/index';
import { useSocket } from '@/hooks/useSocket';
import { adminService } from '@services/adminService';
import { useAuthStore } from '@store/authStore';
import { cn } from '@/utils/cn';
import { useAdminFinancialHealth, useHighRiskCouriers, useSuspendCourier, useActivateCourier } from '@/hooks/useFinance';
import { formatCurrency } from '@/utils/currency';
import { ConfirmModal } from '@/components/organisms';
import Toast from '@/components/atoms/Toast';
import { parsePostGISPoint } from '@/utils/postgis';
import { StakeholderManagementTab } from './admin/StakeholderManagementTab';
import { ContentModerationTab } from './admin/ContentModerationTab';
import { ServiceZonesTab } from './admin/ServiceZonesTab';
import { BannersTab } from './admin/BannersTab';
import { CuisineCategoriesTab } from './admin/CuisineCategoriesTab';
import AnalyticsTab from './admin/AnalyticsTab';
import OrdersTab from './admin/OrdersTab';
import CustomersTab from './admin/CustomersTab';
import FinanceTab from './admin/FinanceTab';
import PlatformSettingsTab from './admin/PlatformSettingsTab';
import SupportTab from './admin/SupportTab';


function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

interface Courier {
  id: string;
  name?: string;
  phone: string;
  status: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
  currentLocation?: {
    latitude: number;
    longitude: number;
  };
  lastLocationUpdateAt?: string;
  activeOrders?: number;
  rating?: number;
}

interface CourierStats {
  total: number;
  available: number;
  busy: number;
  offline: number;
}

type AdminTab = 'dashboard' | 'analytics' | 'orders' | 'customers' | 'finance' | 'stakeholders' | 'zones' | 'moderation' | 'banners' | 'cuisines' | 'settings' | 'support';

const AdminPanelPage = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [stats, setStats] = useState<CourierStats>({
    total: 0,
    available: 0,
    busy: 0,
    offline: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourier, setSelectedCourier] = useState<Courier | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant?: 'default' | 'danger' | 'warning' | 'success';
    onConfirm: () => void;
    courierId?: string;
  }>({
    isOpen: false,
    title: '',
    description: '',
    confirmText: '',
    onConfirm: () => {},
  });
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isVisible: false,
    message: '',
    type: 'info',
  });
  const { isConnected, on, off } = useSocket();
  const financialHealth = useAdminFinancialHealth();
  const highRiskCouriers = useHighRiskCouriers();
  const suspendCourier = useSuspendCourier();
  const activateCourier = useActivateCourier();

  useEffect(() => {
    const { user, isAuthenticated } = useAuthStore.getState();
    console.log('[AdminPanelPage] Mounted', {
      isAuthenticated,
      user: user ? { id: user.id, phone: user.phone, role: user.role } : null,
    });
  }, []);

  const defaultCenter: [number, number] = useMemo(() => [35.3308, 36.9914], []);

  const ADANA_BOUNDS = useMemo(
    () => ({
      latMin: 36.92,
      latMax: 37.06,
      lngMin: 35.22,
      lngMax: 35.45,
    }),
    []
  );

  const isInAdana = useCallback(
    (lat: number, lng: number) => {
      const { latMin, latMax, lngMin, lngMax } = ADANA_BOUNDS;
      return lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax;
    },
    [ADANA_BOUNDS]
  );

  const getFallbackPositionInAdana = useCallback(
    (index: number): [number, number] => {
      const centerLat = 36.9914;
      const centerLng = 35.3308;
      const step = 0.006;
      const row = Math.floor(index / 6);
      const col = index % 6;
      const lat = Math.max(ADANA_BOUNDS.latMin, Math.min(ADANA_BOUNDS.latMax, centerLat + (row - 2) * step));
      const lng = Math.max(ADANA_BOUNDS.lngMin, Math.min(ADANA_BOUNDS.lngMax, centerLng + (col - 2.5) * step));
      return [lng, lat];
    },
    [ADANA_BOUNDS]
  );

  const activeAndBusyCouriers = useMemo(
    () => couriers.filter((c) => c.status === 'AVAILABLE' || c.status === 'BUSY'),
    [couriers]
  );

  const getCourierMapPosition = useCallback(
    (courier: Courier, indexInActiveBusy: number): [number, number] => {
      const loc = courier.currentLocation;
      if (loc && isInAdana(loc.latitude, loc.longitude)) {
        return [loc.longitude, loc.latitude];
      }
      const [lng, lat] = getFallbackPositionInAdana(indexInActiveBusy);
      return [lng, lat];
    },
    [isInAdana, getFallbackPositionInAdana]
  );

  const buildCourierPopupHtml = useCallback((courier: Courier): string => {
    const displayName = courier.name?.trim() || 'Kurye';
    const isAvailable = courier.status === 'AVAILABLE';
    const statusLabel = isAvailable ? 'Müsait' : 'Meşgul';
    const statusBg = isAvailable ? '#d1fae5' : '#fef3c7';
    const statusColor = isAvailable ? '#059669' : '#b45309';
    const initials = displayName
      .split(/\s+/)
      .map((s) => s[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    return `
      <div style="
        font-family: 'Inter', system-ui, sans-serif;
        min-width: 220px;
        padding: 0;
        margin: 0;
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06);
        overflow: hidden;
      ">
        <div style="
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
        ">
          <div style="
            width: 44px;
            height: 44px;
            border-radius: 12px;
            background: linear-gradient(135deg, ${isAvailable ? '#10b981' : '#f59e0b'} 0%, ${isAvailable ? '#059669' : '#d97706'} 100%);
            color: #fff;
            font-weight: 700;
            font-size: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          ">${initials}</div>
          <div style="flex: 1; min-width: 0;">
            <div style="
              font-weight: 700;
              font-size: 15px;
              color: #111827;
              margin-bottom: 4px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            ">${escapeHtml(displayName)}</div>
            <span style="
              display: inline-block;
              padding: 3px 10px;
              border-radius: 9999px;
              font-size: 11px;
              font-weight: 600;
              background: ${statusBg};
              color: ${statusColor};
            ">${statusLabel}</span>
          </div>
        </div>
        <div style="
          padding: 10px 16px 14px;
          border-top: 1px solid #f3f4f6;
          display: flex;
          flex-direction: column;
          gap: 6px;
        ">
          ${courier.phone ? `
            <div style="display: flex; align-items: center; gap: 8px; font-size: 13px; color: #4b5563;">
              <span style="color: #9ca3af;">📞</span>
              <a href="tel:${escapeHtml(courier.phone)}" style="color: #1f2937; text-decoration: none; font-weight: 500;">${escapeHtml(courier.phone)}</a>
            </div>
          ` : ''}
          ${courier.activeOrders !== undefined && courier.activeOrders > 0 ? `
            <div style="display: flex; align-items: center; gap: 8px; font-size: 13px; color: #4b5563;">
              <span style="color: #9ca3af;">📦</span>
              <span style="font-weight: 500; color: #1f2937;">${courier.activeOrders} aktif sipariş</span>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }, []);

  const normalizeString = (str: string): string => {
    return str
      .toLowerCase()
      .replace(/ı/g, 'i')
      .replace(/İ/g, 'i')
      .replace(/ş/g, 's')
      .replace(/Ş/g, 's')
      .replace(/ğ/g, 'g')
      .replace(/Ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/Ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/Ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/Ç/g, 'c');
  };

  const [mapInstance, setMapInstance] = useState<Map | null>(null);

  const handleMapLoad = useCallback((map: Map) => {
    setMapInstance(map);
  }, []);

  useEffect(() => {
    if (!mapInstance || !selectedCourier) return;
    const idx = activeAndBusyCouriers.findIndex((c) => c.id === selectedCourier.id);
    if (idx === -1) return;
    const center = getCourierMapPosition(selectedCourier, idx);
    mapInstance.flyTo({
      center,
      zoom: 15,
      duration: 500,
    });
  }, [mapInstance, selectedCourier, activeAndBusyCouriers, getCourierMapPosition]);

  useEffect(() => {
    loadCouriers({ isBackgroundRefresh: false });
    
    const interval = setInterval(() => loadCouriers({ isBackgroundRefresh: true }), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (suspendCourier.isSuccess) {
      setToast({
        isVisible: true,
        message: 'Kurye başarıyla askıya alındı',
        type: 'success',
      });
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      loadCouriers({ isBackgroundRefresh: true });
    }
    if (suspendCourier.isError) {
      setToast({
        isVisible: true,
        message: 'Kurye askıya alınamadı. Lütfen tekrar deneyin.',
        type: 'error',
      });
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    }
  }, [suspendCourier.isSuccess, suspendCourier.isError]);

  useEffect(() => {
    if (activateCourier.isSuccess) {
      setToast({
        isVisible: true,
        message: 'Kurye başarıyla aktif hale getirildi',
        type: 'success',
      });
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      loadCouriers({ isBackgroundRefresh: true });
    }
    if (activateCourier.isError) {
      setToast({
        isVisible: true,
        message: 'Kurye aktif hale getirilemedi. Lütfen tekrar deneyin.',
        type: 'error',
      });
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    }
  }, [activateCourier.isSuccess, activateCourier.isError]);

  useEffect(() => {
    if (!isConnected) return;

    const handleCourierLocationUpdate = (data: {
      courierId: string;
      location: { latitude: number; longitude: number };
      status: string;
    }) => {
      console.log('📍 COURIER_LOCATION_UPDATE received:', data);
      
      // Update courier location in the list
      setCouriers((prev) =>
        prev.map((courier) =>
          courier.id === data.courierId
            ? {
                ...courier,
                currentLocation: data.location,
                status: data.status as any,
                lastLocationUpdateAt: new Date().toISOString(),
              }
            : courier
        )
      );
    };

    const handleCourierStatusChanged = (data: {
      courierId: string;
      status: string;
      activeOrders: number;
    }) => {
      console.log('👤 COURIER_STATUS_CHANGED received:', data);
      
      // Update courier status in the list
      setCouriers((prev) =>
        prev.map((courier) =>
          courier.id === data.courierId
            ? {
                ...courier,
                status: data.status as any,
                activeOrders: data.activeOrders,
              }
            : courier
        )
      );
    };

    on('COURIER_LOCATION_UPDATE', handleCourierLocationUpdate);
    on('COURIER_STATUS_CHANGED', handleCourierStatusChanged);

    return () => {
      off('COURIER_LOCATION_UPDATE', handleCourierLocationUpdate);
      off('COURIER_STATUS_CHANGED', handleCourierStatusChanged);
    };
  }, [isConnected, on, off]);

  useEffect(() => {
    const newStats: CourierStats = {
      total: couriers.length,
      available: couriers.filter((c) => c.status === 'AVAILABLE').length,
      busy: couriers.filter((c) => c.status === 'BUSY').length,
      offline: couriers.filter((c) => c.status === 'OFFLINE').length,
    };
    setStats(newStats);
  }, [couriers]);

  const mapMarkers = useMemo(() => {
    return activeAndBusyCouriers.map((courier, index) => {
      const isSelected = selectedCourier?.id === courier.id;
      const statusColor =
        courier.status === 'AVAILABLE' ? '#10b981' : '#f59e0b';
      const position = getCourierMapPosition(courier, index);

      return (
        <MapMarker
          key={courier.id}
          position={position}
          color={statusColor}
          size={isSelected ? 40 : 32}
          onClick={() => setSelectedCourier(courier)}
          popupHtml={buildCourierPopupHtml(courier)}
        />
      );
    });
  }, [activeAndBusyCouriers, selectedCourier, getCourierMapPosition]);

  const loadCouriers = async (options?: { isBackgroundRefresh?: boolean }) => {
    const isBackgroundRefresh = options?.isBackgroundRefresh ?? couriers.length > 0;

    if (!isBackgroundRefresh) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const result = await adminService.getCouriers(1, 100);
      const courierList: Courier[] = result.couriers.map((c: any) => {
        let location: { latitude: number; longitude: number } | undefined = undefined;
        
        if (c.currentLocationJson) {
          if (typeof c.currentLocationJson === 'string') {
            const parsed = parsePostGISPoint(c.currentLocationJson);
            if (parsed) {
              location = parsed;
            }
          } else if (typeof c.currentLocationJson === 'object') {
            if ('lat' in c.currentLocationJson && 'lng' in c.currentLocationJson) {
              location = {
                latitude: Number(c.currentLocationJson.lat),
                longitude: Number(c.currentLocationJson.lng),
              };
            } else if ('latitude' in c.currentLocationJson && 'longitude' in c.currentLocationJson) {
              location = {
                latitude: Number(c.currentLocationJson.latitude),
                longitude: Number(c.currentLocationJson.longitude),
              };
            }
          }
        } else if (c.currentLocation && typeof c.currentLocation === 'string') {
          const parsed = parsePostGISPoint(c.currentLocation);
          if (parsed) {
            location = parsed;
          }
        }
        
        let name = 'Kurye';
        if (c.user) {
          if (c.user.name && c.user.surname) {
            name = `${c.user.name} ${c.user.surname}`.trim();
          } else if (c.user.name) {
            name = String(c.user.name).trim();
          } else if (c.user.surname) {
            name = String(c.user.surname).trim();
          } else if (c.user.email) {
            name = String(c.user.email).split('@')[0];
          }
        }
        
        return {
          id: c.id,
          name,
          phone: c.user?.phone || '',
          isOnline: c.isOnline === true,
          status:
            c.status === 'OFFLINE'
                ? 'OFFLINE'
                : c.isOnline === true
                    ? c.status === 'BUSY' || c.status === 'ON_DELIVERY'
                        ? 'BUSY'
                        : 'AVAILABLE'
                    : 'OFFLINE',
          currentLocation: location,
          lastLocationUpdateAt: c.createdAt,
          activeOrders: c.activeOrdersCount || 0,
          rating: typeof c.rating === 'string' ? parseFloat(c.rating) : (c.rating || 0),
        };
      });
      setCouriers(courierList);
    } catch (err) {
      console.error('Load couriers error:', err);
      setError('Kurye verileri yüklenemedi.');
    } finally {
      if (!isBackgroundRefresh) {
        setIsLoading(false);
      }
    }
  };

  const NAV_GROUPS = [
    {
      label: 'Genel',
      items: [
        { id: 'dashboard' as AdminTab, icon: '📊', label: 'Dashboard' },
        { id: 'analytics' as AdminTab, icon: '📈', label: 'Analitik' },
      ],
    },
    {
      label: 'İşlemler',
      items: [
        { id: 'orders' as AdminTab, icon: '📦', label: 'Siparişler' },
        { id: 'customers' as AdminTab, icon: '👥', label: 'Müşteriler' },
      ],
    },
    {
      label: 'İş',
      items: [
        { id: 'finance' as AdminTab, icon: '💰', label: 'Finans' },
        { id: 'stakeholders' as AdminTab, icon: '🏢', label: 'Paydaşlar' },
      ],
    },
    {
      label: 'Platform',
      items: [
        { id: 'zones' as AdminTab, icon: '📍', label: 'Hizmet Bölgeleri' },
        { id: 'moderation' as AdminTab, icon: '✅', label: 'İçerik Denetimi' },
        { id: 'banners' as AdminTab, icon: '🖼️', label: 'Bannerlar' },
        { id: 'cuisines' as AdminTab, icon: '🍽️', label: 'Mutfaklar' },
      ],
    },
    {
      label: 'CRM',
      items: [
        { id: 'support' as AdminTab, icon: '🎧', label: 'Destek Talepleri' },
      ],
    },
    {
      label: 'Sistem',
      items: [
        { id: 'settings' as AdminTab, icon: '⚙️', label: 'Platform Ayarları' },
      ],
    },
  ];

  return (
    <MainLayout>
      <div className="flex -mx-4 -my-8 min-h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 bg-white border-r border-gray-100 flex flex-col shadow-sm">
          {/* Brand */}
          <div className="px-5 pt-7 pb-5 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-[#E63946] flex items-center justify-center shadow-sm">
                <span className="text-white text-sm">🎯</span>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 leading-tight">Admin Paneli</p>
                <p className="text-[10px] text-gray-400 leading-tight">Platform yönetimi</p>
              </div>
            </div>
          </div>

          {/* Nav Groups */}
          <nav className="flex-1 overflow-y-auto py-3 px-3">
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="mb-5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-2 mb-1.5">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm font-medium transition-all duration-150 cursor-pointer mb-0.5',
                        isActive
                          ? 'bg-red-50 text-[#E63946] border-l-[3px] border-[#E63946] pl-[7px]'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-[3px] border-transparent'
                      )}
                    >
                      <span className="text-base leading-none">{item.icon}</span>
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Footer: status + refresh */}
          <div className="px-4 py-4 border-t border-gray-100 space-y-2.5">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-2 h-2 rounded-full shrink-0',
                  isConnected ? 'bg-green-500' : 'bg-red-400'
                )}
              />
              <span className="text-xs text-gray-500">
                {isConnected ? 'Bağlı' : 'Bağlantı Kesildi'}
              </span>
            </div>
            <button
              onClick={loadCouriers}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200 cursor-pointer"
            >
              <span>🔄</span>
              <span>Yenile</span>
            </button>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 py-8 px-8 bg-[#F9FAFB]">
          {/* Tab Content */}
          {activeTab === 'dashboard' && (
            <>
              {/* Page title */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-sm text-gray-500 mt-0.5">Kurye haritası ve platform özeti</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-8">
                <Card>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                    <p className="text-sm text-gray-600">Toplam Kurye</p>
                  </div>
                </Card>
                <Card className="border-success bg-success/5">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-success">{stats.available}</p>
                    <p className="text-sm text-gray-600">Müsait</p>
                  </div>
                </Card>
                <Card className="border-warning bg-warning/5">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-warning">{stats.busy}</p>
                    <p className="text-sm text-gray-600">Meşgul</p>
                  </div>
                </Card>
                <Card className="border-gray-300 bg-gray-50">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-600">{stats.offline}</p>
                    <p className="text-sm text-gray-600">Çevrimdışı</p>
                  </div>
                </Card>
              </div>

              {/* Treasury & Risk */}
              <div className="mb-8 space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">🏦 Sistem Finansal Durumu</h2>
                <p className="text-sm text-gray-600">Platform gelirleri ve nakit riski</p>
              </div>
              {financialHealth.isLoading && <Badge variant="info">Yükleniyor</Badge>}
              {financialHealth.error && <Badge variant="warning">Bağlantı Hatası</Badge>}
            </div>

            {!financialHealth.isLoading && !financialHealth.error && financialHealth.data && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-gray-200">
                  <div className="text-sm text-gray-500 mb-1">Toplam Platform Geliri</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(Number(financialHealth.data.totalPlatformRevenue || 0))}
                  </div>
                </Card>
                <Card className="border-warning/30 bg-warning/10">
                  <div className="text-sm text-gray-600 mb-1">Piyasadaki Toplam Nakit (Risk)</div>
                  <div className="text-2xl font-bold text-warning">
                    {formatCurrency(Number(financialHealth.data.totalCashInMarket || 0))}
                  </div>
                </Card>
              </div>
            )}
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">⚠️ Yüksek Riskli Kuryeler</h3>
                <p className="text-sm text-gray-600">Bakiye limitin altında olan kuryeler</p>
              </div>
              {highRiskCouriers.isLoading && <Badge variant="info">Yükleniyor</Badge>}
              {highRiskCouriers.error && <Badge variant="warning">Bağlantı Hatası</Badge>}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left font-semibold px-6 py-3">Kurye</th>
                    <th className="text-left font-semibold px-6 py-3">Telefon</th>
                    <th className="text-right font-semibold px-6 py-3">Bakiye</th>
                    <th className="text-right font-semibold px-6 py-3">Limit</th>
                    <th className="text-right font-semibold px-6 py-3">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {!highRiskCouriers.isLoading &&
                    !highRiskCouriers.error &&
                    (highRiskCouriers.data?.couriers?.length ?? 0) === 0 && (
                      <tr>
                        <td className="px-6 py-10 text-center text-gray-500" colSpan={5}>
                          Riskli kurye bulunamadı.
                        </td>
                      </tr>
                    )}

                  {(highRiskCouriers.data?.couriers ?? []).map((c) => {
                    const isSuspended = c.status === 'OFFLINE';
                    return (
                      <tr key={c.courierId} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-900 font-medium">
                          {c.name || c.courierId.slice(0, 8)}
                        </td>
                        <td className="px-6 py-3 text-gray-600">{c.phone || '—'}</td>
                        <td className="px-6 py-3 text-right font-bold text-danger">
                          {formatCurrency(Number(c.walletBalance || 0))}
                        </td>
                        <td className="px-6 py-3 text-right text-gray-700">
                          {formatCurrency(Number(c.cashBalanceLimit || 0))}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Button
                            size="sm"
                            variant={isSuspended ? 'default' : 'danger'}
                            disabled={isSuspended || suspendCourier.isPending || activateCourier.isPending}
                            onClick={() => {
                              if (isSuspended) return;
                              setConfirmModal({
                                isOpen: true,
                                title: 'Kuryeyi Askıya Al',
                                description: `${c.name || c.courierId.slice(0, 8)} adlı kuryeyi askıya almak istediğinize emin misiniz? Kurye artık yeni sipariş alamayacak.`,
                                confirmText: 'Askıya Al',
                                variant: 'danger',
                                courierId: c.courierId,
                                onConfirm: () => {
                                  suspendCourier.mutate(c.courierId);
                                },
                              });
                            }}
                          >
                            {isSuspended ? 'Askıya Alındı' : 'Askıya Al'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
                  <p className="text-warning">{error}</p>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="text-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                  <p className="text-gray-600">Kurye konumları yükleniyor...</p>
                </div>
              )}

              {/* Map and Courier List */}
              {!isLoading && (
          <div className="grid grid-cols-3 gap-6">
              {/* Map View (2 columns) */}
            <div className="col-span-2">
              <Card className="p-0 overflow-hidden h-[600px] relative">
                <MapLibreMap
                  center={defaultCenter}
                  zoom={13}
                  onMapLoad={handleMapLoad}
                  className="w-full h-full"
                  showGeolocate={false}
                >
                  {mapMarkers}
                </MapLibreMap>
              </Card>
            </div>

            {/* Courier List (1 column) */}
            <div className="col-span-1">
              <Card className="h-[600px] flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold text-lg mb-3">Kurye Listesi</h3>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="İsim, telefon veya durum ara..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <svg
                      className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <p className="text-xs text-gray-500 mt-2">
                      {couriers.filter((c) => {
                        const query = normalizeString(searchQuery.trim());
                        const name = normalizeString(c.name || '');
                        const phone = normalizeString(c.phone || '');
                        return (
                          name.includes(query) ||
                          phone.includes(query) ||
                          (c.status === 'AVAILABLE' && (normalizeString('müsait').includes(query) || 'available'.includes(query))) ||
                          (c.status === 'BUSY' && (normalizeString('meşgul').includes(query) || 'busy'.includes(query))) ||
                          (c.status === 'OFFLINE' && (normalizeString('çevrimdışı').includes(query) || 'offline'.includes(query)))
                        );
                      }).length} sonuç bulundu
                    </p>
                  )}
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    {couriers
                      .filter((courier) => {
                        if (!searchQuery.trim()) return true;
                        const query = normalizeString(searchQuery.trim());
                        const name = normalizeString(courier.name || '');
                        const phone = normalizeString(courier.phone || '');
                        return (
                          name.includes(query) ||
                          phone.includes(query) ||
                          (courier.status === 'AVAILABLE' && (normalizeString('müsait').includes(query) || 'available'.includes(query))) ||
                          (courier.status === 'BUSY' && (normalizeString('meşgul').includes(query) || 'busy'.includes(query))) ||
                          (courier.status === 'OFFLINE' && (normalizeString('çevrimdışı').includes(query) || 'offline'.includes(query)))
                        );
                      })
                      .map((courier) => (
                    <div
                      key={courier.id}
                      className={cn(
                        'p-3 rounded-lg border-2 cursor-pointer transition-all',
                        selectedCourier?.id === courier.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                      onClick={() => setSelectedCourier(courier)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">
                            {courier.name || 'Kurye'}
                          </p>
                          {courier.phone && (
                            <p className="text-xs text-gray-500">{courier.phone}</p>
                          )}
                        </div>
                        <Badge
                          variant={
                            courier.status === 'AVAILABLE' ? 'success' :
                            courier.status === 'BUSY' ? 'warning' : 'default'
                          }
                        >
                          {courier.status === 'AVAILABLE' ? 'Müsait' :
                           courier.status === 'BUSY' ? 'Meşgul' : 'Çevrimdışı'}
                        </Badge>
                      </div>
                      
                      {courier.activeOrders !== undefined && (
                        <p className="text-sm text-gray-600 mb-1">
                          📦 {courier.activeOrders} aktif sipariş
                        </p>
                      )}
                      
                      {courier.rating && (
                        <p className="text-sm text-gray-600 mb-1">
                          ⭐ {typeof courier.rating === 'number' ? courier.rating.toFixed(1) : parseFloat(String(courier.rating)).toFixed(1)} puan
                        </p>
                      )}
                      
                      {courier.lastLocationUpdateAt && (
                        <p className="text-xs text-gray-400">
                          Son güncelleme:{' '}
                          {new Date(courier.lastLocationUpdateAt).toLocaleTimeString('tr-TR')}
                        </p>
                      )}
                      
                      {courier.status === 'OFFLINE' && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <Button
                            size="sm"
                            variant="success"
                            className="w-full"
                            disabled={activateCourier.isPending || suspendCourier.isPending}
                            onClick={() => {
                              setConfirmModal({
                                isOpen: true,
                                title: 'Kuryeyi Aktif Et',
                                description: `${courier.name || 'Kurye'} adlı kuryeyi aktif hale getirmek istediğinize emin misiniz? Kurye artık yeni sipariş alabilecek.`,
                                confirmText: 'Aktif Et',
                                variant: 'success',
                                courierId: courier.id,
                                onConfirm: () => {
                                  activateCourier.mutate(courier.id);
                                },
                              });
                            }}
                          >
                            ✅ Aktif Et
                          </Button>
                        </div>
                      )}
                    </div>
                      ))}
                    {couriers.filter((c) => {
                      if (!searchQuery.trim()) return false;
                      const query = normalizeString(searchQuery.trim());
                      const name = normalizeString(c.name || '');
                      const phone = normalizeString(c.phone || '');
                      return (
                        name.includes(query) ||
                        phone.includes(query) ||
                        (c.status === 'AVAILABLE' && (normalizeString('müsait').includes(query) || 'available'.includes(query))) ||
                        (c.status === 'BUSY' && (normalizeString('meşgul').includes(query) || 'busy'.includes(query))) ||
                        (c.status === 'OFFLINE' && (normalizeString('çevrimdışı').includes(query) || 'offline'.includes(query)))
                      );
                    }).length === 0 && searchQuery && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">Arama sonucu bulunamadı</p>
                        <p className="text-xs mt-1">Farklı bir terim deneyin</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
              )}
            </>
          )}

          {activeTab === 'analytics' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Analitik</h1>
                <p className="text-sm text-gray-500 mt-0.5">Platform performans metrikleri</p>
              </div>
              <AnalyticsTab />
            </>
          )}

          {activeTab === 'orders' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Siparişler</h1>
                <p className="text-sm text-gray-500 mt-0.5">Tüm sipariş yönetimi</p>
              </div>
              <OrdersTab />
            </>
          )}

          {activeTab === 'customers' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Müşteriler</h1>
                <p className="text-sm text-gray-500 mt-0.5">Kullanıcı yönetimi ve inceleme</p>
              </div>
              <CustomersTab />
            </>
          )}

          {activeTab === 'finance' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Finans</h1>
                <p className="text-sm text-gray-500 mt-0.5">Finansal işlemler ve mutabakat</p>
              </div>
              <FinanceTab />
            </>
          )}

          {activeTab === 'stakeholders' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Paydaşlar</h1>
                <p className="text-sm text-gray-500 mt-0.5">Restoran ve kurye yönetimi</p>
              </div>
              <StakeholderManagementTab />
            </>
          )}

          {activeTab === 'zones' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Hizmet Bölgeleri</h1>
                <p className="text-sm text-gray-500 mt-0.5">Teslimat bölgesi tanımları</p>
              </div>
              <ServiceZonesTab />
            </>
          )}

          {activeTab === 'moderation' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">İçerik Denetimi</h1>
                <p className="text-sm text-gray-500 mt-0.5">Yorum ve içerik moderasyonu</p>
              </div>
              <ContentModerationTab />
            </>
          )}

          {activeTab === 'banners' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Bannerlar</h1>
                <p className="text-sm text-gray-500 mt-0.5">Uygulama içi banner yönetimi</p>
              </div>
              <BannersTab />
            </>
          )}

          {activeTab === 'cuisines' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Mutfaklar</h1>
                <p className="text-sm text-gray-500 mt-0.5">Mutfak kategorisi yönetimi</p>
              </div>
              <CuisineCategoriesTab />
            </>
          )}

          {activeTab === 'settings' && <PlatformSettingsTab />}

          {activeTab === 'support' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Destek Talepleri</h1>
                <p className="text-sm text-gray-500 mt-0.5">Müşteri destek taleplerini yönetin</p>
              </div>
              <SupportTab />
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        description={confirmModal.description}
        confirmText={confirmModal.confirmText}
        cancelText="Vazgeç"
        variant={confirmModal.variant}
        isConfirmLoading={suspendCourier.isPending || activateCourier.isPending}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
    </MainLayout>
  );
};

export default AdminPanelPage;
